import ApiService from './SupabaseApiService';
import { supabaseHelpers, supabase } from './supabase';

/**
 * Task Scheduler Service
 * Automatically creates recurring tasks based on patterns
 */
class TaskScheduler {
  constructor() {
    this.isRunning = false;
    this.checkInterval = null;
  }

  /**
   * Start the scheduler (checks for recurring tasks daily)
   */
  start() {
    if (this.isRunning) {
      console.log('⚠️ Task scheduler already running');
      return;
    }

    console.log('🔄 Starting task scheduler...');
    this.isRunning = true;

    // Check immediately on start
    this.checkAndCreateRecurringTasks();

    // Then check every hour (tasks will only be created once per day)
    this.checkInterval = setInterval(() => {
      this.checkAndCreateRecurringTasks();
    }, 60 * 60 * 1000); // Check every hour
  }

  /**
   * Stop the scheduler
   */
  stop() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    this.isRunning = false;
    console.log('⏹️ Task scheduler stopped');
  }

  /**
   * Check for recurring tasks and create instances if needed
   */
  async checkAndCreateRecurringTasks() {
    try {
      console.log('📅 Checking for recurring tasks...');
      
      const { data: recurringTasks, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('recurring', true)
        .eq('archived', false)
        .eq('completed', false);

      if (error) {
        console.error('❌ Error fetching recurring tasks:', error);
        return;
      }

      if (!recurringTasks || recurringTasks.length === 0) {
        console.log('✅ No recurring tasks found');
        return;
      }

      console.log(`📋 Found ${recurringTasks.length} recurring tasks`);

      const today = new Date();
      const dayOfWeek = today.getDay(); // 0 = Sunday, 6 = Saturday
      const todayDate = today.toISOString().split('T')[0];

      let createdCount = 0;

      for (const task of recurringTasks) {
        try {
          // Check if task instance already exists for today
          const existingInstance = await this.checkTaskInstanceExists(task.id, todayDate);
          
          if (existingInstance) {
            console.log(`⏭️ Task "${task.title}" already has instance for today`);
            continue;
          }

          // Check if task should be created today based on pattern
          const shouldCreate = this.shouldCreateTaskToday(task, dayOfWeek, todayDate);
          
          if (shouldCreate) {
            await this.createTaskInstance(task, todayDate);
            createdCount++;
            console.log(`✅ Created instance for recurring task: "${task.title}"`);
          }
        } catch (error) {
          console.error(`❌ Error processing task ${task.id}:`, error);
        }
      }

      if (createdCount > 0) {
        console.log(`🎉 Created ${createdCount} task instance(s) for today`);
      }
    } catch (error) {
      console.error('❌ Error in checkAndCreateRecurringTasks:', error);
    }
  }

  /**
   * Check if task instance already exists for a given date
   */
  async checkTaskInstanceExists(taskId, date) {
    try {
      const { data, error } = await supabase
        .from('task_instances')
        .select('id')
        .eq('parent_task_id', taskId)
        .eq('instance_date', date)
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error checking task instance:', error);
        return false;
      }

      return !!data;
    } catch (error) {
      // If table doesn't exist, fall back to checking tasks table
      // This allows graceful degradation
      console.warn('Task instances table may not exist, using fallback check');
      return false;
    }
  }

  /**
   * Determine if a task should be created today based on its pattern
   */
  shouldCreateTaskToday(task, dayOfWeek, todayDate) {
    const pattern = task.recurring_pattern;
    const recurringData = task.recurring_data || {};

    switch (pattern) {
      case 'daily':
        return true;

      case 'weekdays':
        // Monday (1) to Friday (5)
        return dayOfWeek >= 1 && dayOfWeek <= 5;

      case 'weekends':
        // Saturday (6) or Sunday (0)
        return dayOfWeek === 0 || dayOfWeek === 6;

      case 'custom':
        // Check if today is in the custom days array
        const customDays = recurringData.customDays || [];
        return customDays.includes(dayOfWeek);

      case 'weekly':
        // Check if today matches the weekly day
        const weeklyDay = recurringData.dayOfWeek;
        return weeklyDay === dayOfWeek;

      default:
        return false;
    }
  }

  /**
   * Create a new task instance for today
   */
  async createTaskInstance(parentTask, date) {
    try {
      // Create a new task instance (or use task_instances table if it exists)
      const taskData = {
        kid_id: parentTask.kid_id,
        family_id: parentTask.family_id,
        title: parentTask.title,
        description: parentTask.description,
        points: parentTask.points,
        category: parentTask.category,
        recurring: false, // This instance is not recurring
        parent_task_id: parentTask.id, // Link to parent recurring task
        due_date: this.calculateDueDate(parentTask, date),
        reminder_time: parentTask.reminder_time,
        requires_verification: parentTask.requires_verification,
        photo_required: parentTask.photo_required,
        priority: parentTask.priority,
        estimated_minutes: parentTask.estimated_minutes,
        created_by: parentTask.created_by,
        assigned_by: parentTask.assigned_by,
        metadata: {
          ...parentTask.metadata,
          instance_date: date,
          is_recurring_instance: true
        }
      };

      const result = await supabaseHelpers.addTask(parentTask.kid_id, taskData);
      return result;
    } catch (error) {
      console.error('Error creating task instance:', error);
      throw error;
    }
  }

  /**
   * Calculate due date for a task instance
   */
  calculateDueDate(parentTask, instanceDate) {
    if (!parentTask.due_date) {
      return null;
    }

    // If parent task has a due_date, use the time portion
    // but set the date to today
    const parentDueDate = new Date(parentTask.due_date);
    const instanceDateObj = new Date(instanceDate);
    
    instanceDateObj.setHours(parentDueDate.getHours());
    instanceDateObj.setMinutes(parentDueDate.getMinutes());
    instanceDateObj.setSeconds(0);
    instanceDateObj.setMilliseconds(0);

    return instanceDateObj.toISOString();
  }

  /**
   * Manually trigger task creation for a specific date
   */
  async createTasksForDate(date) {
    console.log(`📅 Creating tasks for date: ${date}`);
    // Implementation similar to checkAndCreateRecurringTasks
    // but for a specific date
  }
}

// Export singleton instance
const taskScheduler = new TaskScheduler();
export default taskScheduler;

